import { Ionicons } from '@expo/vector-icons';
import React from "react";
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  FlatList,
  ActivityIndicator,
  StyleSheet,
} from "react-native";

import PlayerAvatar from "../../../components/PlayerAvatar";
import { useAppTheme } from "../../../design-system/ThemeProvider";
import { t } from "../../../i18n/translations";
import { useLanguageStore } from "../../quiz-core/languageStore";
import type { GameRoom, GamePlayer } from "../types/game.types";

interface PlayerPreviewModalProps {
  visible: boolean;
  roomName: string;
  players: GamePlayer[];
  currentRoom: GameRoom | null;
  loading: boolean;
  onClose: () => void;
}

const PlayerPreviewModal: React.FC<PlayerPreviewModalProps> = ({
  visible,
  roomName,
  players,
  currentRoom,
  loading,
  onClose,
}) => {
  const { language } = useLanguageStore();
  const { colors } = useAppTheme();

  const formatJoinTime = (joinedAt?: string) => {
    if (!joinedAt) return null;

    try {
      return new Date(joinedAt).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return null;
    }
  };

  return (
    <Modal
      animationType="slide"
      transparent={true}
      visible={visible}
      onRequestClose={onClose}
      data-oid="r1tg419"
    >
      <View
        style={[styles.overlay, { backgroundColor: "rgba(10, 8, 14, 0.72)" }]}
        data-oid="wmpvwp1"
      >
        <View
          style={[
            styles.modalCard,
            {
              backgroundColor: colors.surface,
              borderColor: colors.outlineVariant,
              shadowColor: colors.black,
            },
          ]}
          data-oid="a5-86k."
        >
          <View
            style={styles.headerRow}
            data-oid="yk0kl5i"
          >
            <View style={styles.titleBlock}>
              <Text
                style={[styles.eyebrow, { color: colors.primary }]}
                data-oid="h16bult"
              >
                {t('multiplayer_lobby.activeRooms', language)}
              </Text>
              <Text style={[styles.title, { color: colors.onSurface }]}>
                {t('multiplayer_lobby.roomPlayersTitle', language)?.replace('{{roomName}}', roomName) || `Players in ${roomName}`}
              </Text>
            </View>
            <TouchableOpacity
              onPress={onClose}
              style={[
                styles.closeButton,
                {
                  backgroundColor: colors.surfaceContainerHigh,
                  borderColor: colors.outlineVariant,
                },
              ]}
              data-oid="d.sh:4l"
            >
              <Ionicons name="close" size={18} color={colors.onSurface} />
            </TouchableOpacity>
          </View>

          {loading ? (
            <View style={styles.loadingState} data-oid="bjzqjtt">
              <ActivityIndicator
                size="large"
                color={colors.primary}
                data-oid="3lx0aqu"
              />

              <Text style={[styles.loadingText, { color: colors.onSurfaceVariant }]} data-oid="_89x4d5">
                {t('multiplayer_lobby.loadingPlayers', language)}
              </Text>
            </View>
          ) : players.length > 0 ? (
            <FlatList
              data={players}
              keyExtractor={(item) => item.id}
              renderItem={({ item }) => {
                const isHost = item.user_id === currentRoom?.host_id;
                const joinedLabel = formatJoinTime(item.joined_at);
                return (
                  <View
                    style={[
                      styles.playerRow,
                      {
                        backgroundColor: isHost
                          ? colors.surfaceContainerHighest
                          : colors.surfaceContainerLow,
                        borderColor: isHost ? colors.primary : colors.outlineVariant,
                      },
                    ]}
                    data-oid="xq_2::9"
                  >
                    <View style={styles.avatarWrap} data-oid="02u8.57">
                      <PlayerAvatar
                        nickname={item.nickname ?? item.name ?? "Player"}
                        size="md"
                        profilePictureUrl={item.avatar_url}
                        data-oid="t_bj2m_"
                      />

                      {isHost && (
                        <View
                          style={[styles.hostBadge, { backgroundColor: colors.primary }]}
                          data-oid="tqdkvhh"
                        >
                          <Ionicons name="star" size={12} color="#FFFFFF" data-oid="jrl8z9p" />
                        </View>
                      )}
                    </View>
                    <View style={styles.playerMeta} data-oid="la_xwfk">
                      <View
                        style={styles.playerNameRow}
                        data-oid="7203i6:"
                      >
                        <Text
                          style={[styles.playerName, { color: colors.onSurface }]}
                          data-oid="y:ose.d"
                        >
                          {item.name ?? 'Player'}
                        </Text>
                        {isHost && (
                          <Text
                            style={[styles.hostLabel, { color: colors.primary }]}
                            data-oid="joxl3qz"
                          >
                            {t('multiplayer_lobby.host', language)}
                          </Text>
                        )}
                      </View>
                      <View
                        style={styles.badgesRow}
                        data-oid="mjm9u:8"
                      >
                        <View
                          style={[
                            styles.statusPill,
                            {
                              backgroundColor: item.is_ready
                                ? colors.correctLight
                                : colors.surfaceContainerHigh,
                            },
                          ]}
                          data-oid="kt:-kaq"
                        >
                          <Text
                            style={[
                              styles.statusText,
                              {
                                color: item.is_ready ? colors.correctDark : colors.onSurfaceVariant,
                              },
                            ]}
                            data-oid="scitc4:"
                          >
                            {item.is_ready ? t('multiplayer_lobby.ready', language) : t('multiplayer_lobby.waiting', language)}
                          </Text>
                        </View>
                        {item.streak && item.streak > 0 && (
                          <View
                            style={[styles.statusPill, { backgroundColor: colors.accentLight }]}
                            data-oid="exyyut0"
                          >
                            <Text
                              style={[styles.statusText, { color: colors.accentDark }]}
                              data-oid="3_w9v9h"
                            >
                              {t('multiplayer_lobby.dayStreak', language)?.replace('{count}', item.streak.toString()) || `${item.streak} day streak`}
                            </Text>
                          </View>
                        )}
                        {item.language && (
                          <View
                            style={[styles.statusPill, { backgroundColor: colors.surfaceContainerHigh }]}
                            data-oid="dsakwuu"
                          >
                            <Text
                              style={[styles.statusText, { color: colors.onSurfaceVariant }]}
                              data-oid="ae8294s"
                            >
                              🌐 {item.language?.toUpperCase()}
                            </Text>
                          </View>
                        )}
                      </View>
                    </View>
                    <View
                      style={styles.joinTimeWrap}
                      data-oid="tcv2m3w"
                    >
                      {joinedLabel ? (
                        <Text
                          style={[styles.joinTimeText, { color: colors.onSurfaceVariant }]}
                          data-oid="vniox9-"
                        >
                          {joinedLabel}
                        </Text>
                      ) : null}
                    </View>
                  </View>
                );
              }}
              ListEmptyComponent={
                <Text style={[styles.emptyText, { color: colors.onSurfaceVariant }]} data-oid="b.gm181">
                  {t('multiplayer_lobby.noPlayersYet', language)}
                </Text>
              }
              data-oid="ywedbwy"
            />
          ) : (
            <View style={styles.loadingState} data-oid="1lynmp1">
              <Text style={[styles.emptyText, { color: colors.onSurfaceVariant }]} data-oid="8n.093w">
                {t('multiplayer_lobby.noPlayersYet', language)}
              </Text>
            </View>
          )}

          <View style={styles.footerRow} data-oid="25reat5">
            <TouchableOpacity
              style={[
                styles.footerButton,
                {
                  backgroundColor: colors.primary,
                  shadowColor: colors.primary,
                },
              ]}
              onPress={onClose}
              data-oid="scbwxn0"
            >
              <Text style={styles.footerButtonText} data-oid="vwv9_4y">
                {t('multiplayer_lobby.close', language)}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 20,
  },
  modalCard: {
    width: "100%",
    maxHeight: "76%",
    borderRadius: 28,
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 16,
    borderWidth: 1,
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.18,
    shadowRadius: 20,
    elevation: 10,
  },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 18,
  },
  titleBlock: {
    flex: 1,
    paddingRight: 12,
  },
  eyebrow: {
    fontSize: 12,
    fontWeight: "700",
    letterSpacing: 0.6,
    textTransform: "uppercase",
    marginBottom: 6,
  },
  title: {
    fontSize: 22,
    fontWeight: "800",
    lineHeight: 28,
  },
  closeButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
  },
  loadingState: {
    paddingVertical: 32,
    alignItems: "center",
    justifyContent: "center",
  },
  loadingText: {
    marginTop: 16,
    fontSize: 15,
  },
  playerRow: {
    flexDirection: "row",
    alignItems: "center",
    padding: 14,
    marginBottom: 10,
    borderRadius: 22,
    borderWidth: 1,
  },
  avatarWrap: {
    position: "relative",
  },
  hostBadge: {
    position: "absolute",
    top: -2,
    right: -2,
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
  },
  playerMeta: {
    flex: 1,
    marginLeft: 12,
  },
  playerNameRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
  },
  playerName: {
    fontSize: 16,
    fontWeight: "700",
  },
  hostLabel: {
    fontSize: 12,
    fontWeight: "700",
    marginLeft: 8,
  },
  badgesRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 8,
  },
  statusPill: {
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 4,
    marginRight: 8,
    marginBottom: 6,
  },
  statusText: {
    fontSize: 11,
    fontWeight: "700",
  },
  joinTimeWrap: {
    marginLeft: 8,
    alignItems: "flex-end",
    justifyContent: "center",
  },
  joinTimeText: {
    fontSize: 11,
    fontWeight: "600",
  },
  emptyText: {
    textAlign: "center",
    paddingVertical: 16,
    fontSize: 15,
  },
  footerRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 16,
  },
  footerButton: {
    borderRadius: 16,
    paddingHorizontal: 18,
    paddingVertical: 12,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.18,
    shadowRadius: 14,
    elevation: 4,
  },
  footerButtonText: {
    color: "#FFFFFF",
    fontWeight: "800",
    fontSize: 14,
  },
});

export default PlayerPreviewModal;
